import { keepPreviousData, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { observer } from 'mobx-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'

import rescueXIcon from '@/assets/images/icon.png'
import { CertificateRequirements } from '@/components/CertificateRequirements'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
// Service & Utils
import { DataTableFilter } from '@/components/DataTable/DataTableFilter'
import { Tabs, TabsList, TabsTrigger } from '@/components/elements/tabs'
// Components
import { Layout } from '@/components/Layout'
import { TextLink } from '@/components/TextLink'
import { getCourses } from '@/services/courses.service'
// Store
import { getEntityCertificates } from '@/services/entity_certificates.service'

// The RescueX / Facility tabs drive the is_xverse_certificate filter server-side,
// so it always carries a value — default to the RescueX tab ('true').
export const DEFAULT_FILTERS = {
  course: '',
  is_xverse_certificate: 'true',
}

const InstructorCertificates = observer(() => {
  // Context
  const { entityId } = useParams()
  // State
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })

  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  // RescueX ('true') vs. Facility ('false') tabs swap the is_xverse_certificate
  // filter and reset to the first page so we never land out of range.
  const handleTabChange = (value) => {
    setFilters((prev) => ({ ...prev, is_xverse_certificate: value }))
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  // Data Processing
  const { data: courses } = useQuery({
    queryKey: ['courses', entityId],
    queryFn: () => getCourses(null, null, null, null, true),
    placeholderData: keepPreviousData,
  })
  const { data: certificates, isPending } = useQuery({
    queryKey: ['accounts', 'instructors', 'certificates', entityId, sorting, pagination, filters],
    queryFn: () =>
      getEntityCertificates(entityId, {
        sorting,
        pagination,
        searchTerm: filters.search,
        filters: _.omit(filters, 'search'),
      }),
    placeholderData: keepPreviousData,
  })

  const COURSES = useMemo(() => {
    if (!courses) return []

    return courses.results.map((course) => ({
      label: course.name,
      id: course.id,
    }))
  }, [courses])

  const columns = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
      size: 300,
      cell: ({ getValue, row }) => <TextLink to={`${row.original.id}`}>{getValue()}</TextLink>,
    },
    {
      id: 'requirements',
      header: () => <span className="uppercase">Requirements</span>,
      cell: ({ row }) => <CertificateRequirements certificate={row.original} />,
      sortable: false,
    },
    {
      header: 'Enrolled',
      accessorKey: 'numEnrolledStudents',
    },
    {
      header: 'Granted',
      accessorKey: 'numCertifiedStudents',
    },
  ]

  return (
    <Layout breadcrumbs={['Instructor', 'Course Assignments']}>
      <Layout.Heading title="Course Assignments" />
      <Tabs
        value={filters.is_xverse_certificate}
        onValueChange={handleTabChange}
        className="flex flex-col gap-4"
        data-testid="instructors-certificates-page"
      >
        <TabsList className="self-start">
          <TabsTrigger value="true">
            <img src={rescueXIcon} alt="" className="mr-1.5 h-4 w-4 shrink-0" />
            RescueX Courses
          </TabsTrigger>
          <TabsTrigger value="false">Facility Courses</TabsTrigger>
        </TabsList>

        <DataTableFilter
          setFilters={setFilters}
          filterValues={filters}
          filters={[
            { type: 'input', label: 'Search Courses', id: 'search' },
            {
              type: 'multiselect',
              label: 'Filter by Course Module',
              id: 'course',
              options: COURSES,
            },
          ]}
        />

        <DataTable
          columns={columns}
          data={certificates?.results || []}
          loading={isPending}
          pagination={pagination}
          rowCount={certificates?.count || 0}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
        />
      </Tabs>
    </Layout>
  )
})

export default InstructorCertificates
